import { useState } from 'react'
import { Botao } from '../../components/Botao'
import { AvisoDeErro } from '../../components/Estados'
import { CampoDeSelecao, CampoDeTexto, CampoDeTextoLongo } from '../../components/campos'
import { TEXTOS } from '../../lib/glossario'
import { useRegistrarOcorrencia } from '../../api/queries/iad'
import type { ItemDoAnexo } from '../../types/iad'

/**
 * O registro de uma ocorrência administrativa.
 *
 * **A pontuação não é campo.** Ela sai da tabela do contrato assim que o item é escolhido, e
 * aparece só para conferência. Se fosse digitável, a glosa passaria a depender de quem digita — e a
 * contratada não teria como contestar o número contra o Anexo VIII.
 *
 * **A data do fato é separada do mês da apuração.** Uma ocorrência de 30/06 descoberta em 05/07 é
 * apurada em julho, com a data real ao lado: é o que permite fechar junho sem esconder o que se
 * descobriu depois.
 *
 * @param props o mês em apuração e os itens do contrato
 * @returns o formulário
 */
export function FormularioDeOcorrencia({ competencia, itens }: { competencia: string; itens: ItemDoAnexo[] }) {
  const t = TEXTOS.iad
  const registrar = useRegistrarOcorrencia()
  const [item, setItem] = useState('')
  const [ocorridoEm, setOcorridoEm] = useState('')
  const [descricao, setDescricao] = useState('')

  const escolhido = itens.find((i) => String(i.item) === item)
  const completo = escolhido != null && ocorridoEm.length > 0 && descricao.trim().length > 0

  function enviar() {
    if (!escolhido) {
      return
    }
    registrar.mutate(
      { competencia, item: escolhido.item, ocorridoEm, descricao: descricao.trim() },
      {
        onSuccess: () => {
          setItem('')
          setOcorridoEm('')
          setDescricao('')
        },
      },
    )
  }

  return (
    <section className="cartao space-y-4 p-4" aria-label={t.registrar}>
      <h2 className="text-base font-semibold text-slate-900">{t.registrar}</h2>

      <CampoDeSelecao
        rotulo="Qual descumprimento"
        ajuda="Os 27 itens do Anexo VIII, com a pontuação que o contrato fixa para cada um."
        value={item}
        onChange={(e) => setItem(e.target.value)}
      >
        <option value="">Escolha o item…</option>
        {itens.map((i) => (
          <option key={i.item} value={i.item}>
            {i.item}. {i.infracao}
          </option>
        ))}
      </CampoDeSelecao>

      {escolhido && (
        <p className="rounded-lg bg-slate-50 px-3 py-2 text-xs text-slate-700">
          <span className="font-semibold tabular-nums">{escolhido.pontos} pontos</span> por ocorrência ·{' '}
          {escolhido.fonteDoTr}
        </p>
      )}

      <CampoDeTexto
        rotulo="Quando aconteceu"
        ajuda="A data do fato. Pode ser de outro mês — a ocorrência entra na apuração deste."
        type="date"
        value={ocorridoEm}
        onChange={(e) => setOcorridoEm(e.target.value)}
      />

      <CampoDeTextoLongo
        rotulo="O que aconteceu"
        ajuda="É o texto que a contratada vai ler se contestar. Sem ele, a ocorrência é um número sem fato."
        rows={3}
        value={descricao}
        onChange={(e) => setDescricao(e.target.value)}
      />

      {registrar.isError && <AvisoDeErro erro={registrar.error} />}

      <Botao aparencia="primario" onClick={enviar} disabled={!completo || registrar.isPending}>
        {registrar.isPending ? t.registrando : t.registrar}
      </Botao>
    </section>
  )
}
